import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useScrollAnimation, useScrollAnimationMultiple } from "@/hooks/useScrollAnimation";
import Login from "@/components/Login";
import WelcomeDialog from "@/components/WelcomeDialog";
import Navbar from "@/components/Navbar";
import HeroCasos from "@/components/HeroCasos";
import CasosFeatures from "@/components/CasosFeatures";
import { Helmet } from "react-helmet-async";
import { Instagram, Linkedin, FileText, Download, Edit3, Clock, Shield, Users, CheckCircle, MessageSquare, Zap, FileCheck, CreditCard, Star, X, Infinity, Mail, Coins, Briefcase, Calculator, Database, Share2, History, FileEdit } from "lucide-react";
import LegalDocumentDialog from "@/components/LegalDocumentDialog";
import TermsOfUseContent from "@/components/legal/TermsOfUse";
import AITermsContent from "@/components/legal/AITerms";
import PrivacyPolicyContent from "@/components/legal/PrivacyPolicy";
import LGPDComplianceContent from "@/components/legal/LGPDCompliance";
import SecurityInfoContent from "@/components/legal/SecurityInfo";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import { ParticleTextEffect } from "@/components/ui/particle-text-effect";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ExpandableTabs, { ExpandableTab } from "@/components/ui/ExpandableTabs";
import "@/styles/expandable-tabs.css";

import { PlansSection } from "@/components/PlansSection";
const Index = () => {
  const {
    user,
    isLoading
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const redirectInProgress = useRef(false);
  const [showWelcome, setShowWelcome] = useState(false);

  // Legal document dialogs state
  const [showTermsOfUse, setShowTermsOfUse] = useState(false);
  const [showAITerms, setShowAITerms] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showLGPDCompliance, setShowLGPDCompliance] = useState(false);
  const [showSecurityInfo, setShowSecurityInfo] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);

  // Scroll animations
  const heroTitle = useScrollAnimation();
  const heroSubtitle = useScrollAnimation();
  const heroLogin = useScrollAnimation();
  const heroChat = useScrollAnimation();
  const heroFeatures = useScrollAnimation();
  const pricingSection = useScrollAnimation();
  const {
    setRef: setPricingCardRef,
    visibleItems: pricingCardsVisible
  } = useScrollAnimationMultiple(4);
  const featuresSection = useScrollAnimation();
  const {
    setRef: setFeatureCardRef,
    visibleItems: featureCardsVisible
  } = useScrollAnimationMultiple(6);
  const howItWorksSection = useScrollAnimation();
  const {
    setRef: setStepRef,
    visibleItems: stepsVisible
  } = useScrollAnimationMultiple(3);
  const benefitsSection = useScrollAnimation();
  const {
    setRef: setBenefitRef,
    visibleItems: benefitsVisible
  } = useScrollAnimationMultiple(4);
  const ethicsSection = useScrollAnimation();
  const ctaSection = useScrollAnimation();
  const footerSection = useScrollAnimation();
  const tabsSection = useScrollAnimation();

  // Parallax effect for hero overlay
  const [parallaxOffset, setParallaxOffset] = useState(0);
  const heroSectionRef = useRef<HTMLElement>(null);

  // Expandable Tabs Data
  const MAIN_TABS: ExpandableTab[] = [{
    id: "chat",
    label: "Chat Jurídico",
    href: "/chat-juridico",
    description: "Converse com documentos e obtenha respostas jurídicas precisas com nossa IA especializada em direito brasileiro.",
    icon: <MessageSquare className="w-5 h-5" />
  }, {
    id: "docs",
    label: "Documentos",
    href: "/documentos",
    description: "Crie e gerencie peças processuais com templates profissionais e papel timbrado personalizado.",
    icon: <FileText className="w-5 h-5" />
  }, {
    id: "calculos",
    label: "Cálculos",
    href: "/calculos",
    description: "Ferramentas de cálculo jurídico: trabalhistas, revisões, INSS, FGTS e muito mais.",
    icon: <Calculator className="w-5 h-5" />
  }, {
    id: "casos",
    label: "Casos",
    href: "/casos",
    description: "Organize e gerencie seus processos com documentos e fontes centralizadas.",
    icon: <Briefcase className="w-5 h-5" />
  }];
  useEffect(() => {
    // If hash contains auth params, let AuthContext handle it
    if (window.location.hash && window.location.hash.includes('access_token')) {
      console.log("Parâmetros de autenticação detectados na página inicial, processando...", {
        hash: window.location.hash.substring(0, 20) + '...',
        pathname: location.pathname,
        fullUrl: window.location.href
      });
      return;
    }

    // CORREÇÃO: Só redirecionar se estiver NA RAIZ e não tiver intenção específica
    if (user && !isLoading && !redirectInProgress.current && location.pathname === '/') {
      console.log("Usuário autenticado na página raiz, redirecionando para dashboard...");
      redirectInProgress.current = true;

      // Show welcome dialog for new users
      if (!localStorage.getItem('welcomed')) {
        setShowWelcome(true);
        localStorage.setItem('welcomed', 'true');
      } else {
        navigate('/casos');
      }
    }
    return () => {
      redirectInProgress.current = false;
    };
  }, [user, isLoading, navigate, location.pathname]);
  useEffect(() => {
    const cookieConsentAccepted = localStorage.getItem('cookie-consent-accepted');
    if (!cookieConsentAccepted) {
      setShowCookieConsent(true);
    }
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      if (heroSectionRef.current) {
        const scrolled = window.scrollY;
        // Parallax effect: clouds move at 30% of scroll speed
        setParallaxOffset(scrolled * 0.3);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen w-full">
      <Helmet>
        <title>Justiçai - IA Jurídica com organização de escritório</title>
        <meta name="description" content="Tecnologia de ponta para escritórios de advocacia. IA especializada em direito brasileiro com total conformidade ética e LGPD. Análise de documentos, peças processuais e papel timbrado personalizado." />
        <meta name="keywords" content="IA jurídica, inteligência artificial advocacia, tecnologia jurídica, conformidade LGPD, ética OAB, análise documentos jurídicos, peças processuais IA, papel timbrado digital, automação escritório advocacia, direito brasileiro" />
        <meta property="og:title" content="Justiçai - IA Jurídica com organização de escritório" />
        <meta property="og:description" content="Seu escritório merece tecnologia de ponta. IA especializada em direito brasileiro com total conformidade ética e LGPD para acelerar o trabalho jurídico." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://justicai.laib.com.br" />
      </Helmet>
      
      <div className="min-h-screen flex flex-col bg-background">
        {/* Navbar */}
        <Navbar data-tour="navbar" />
        
        {/* Hero Section 2 - Layout with Illustration */}
        <section id="home" data-tour="hero-section" ref={heroSectionRef} className="hero-section-new pt-24 md:pt-32">
          <div className="relative container mx-auto px-4 py-16 md:py-24" style={{
          zIndex: 2
        }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              
              {/* LEFT SIDE: Illustration */}
              <div className="order-2 lg:order-1">
                <div ref={heroChat.ref} className={`hero-2-img transition-all duration-700 delay-300 ${heroChat.isVisible ? 'animate-slide-up' : 'opacity-0 translate-y-8'}`}>
                  <img src="/hero-2-img.png" alt="Justiçai - Inteligência Artificial Jurídica" className="w-full h-auto" />
                </div>
              </div>
              
              {/* RIGHT SIDE: Content */}
              <div className="order-1 lg:order-2 space-y-6 text-center">
                {/* Small Purple Tag */}
                
                
                {/* Main Title */}
                <h1 ref={heroSubtitle.ref} className={`text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-gray-900 transition-all duration-700 delay-150 ${heroSubtitle.isVisible ? 'animate-slide-up-delay-1' : 'opacity-0 translate-y-8'}`}>
                  IA 100% Focada<br />
                  no Direto Brasileiro
                </h1>
                
                {/* Description */}
                <p className={`text-lg text-gray-600 transition-all duration-700 delay-300 ${heroSubtitle.isVisible ? 'animate-slide-up-delay-2' : 'opacity-0 translate-y-8'}`}><strong>Seu escritório merece tecnologia de ponta.</strong> IA especializada em direito brasileiro para análise de documentos e criação de peças processuais, com <strong>total conformidade ética e LGPD</strong>. Mantemos os advogados no controle, apenas aceleramos o trabalho.
                </p>
                
                {/* Login Card */}
                <div ref={heroLogin.ref} className={`bg-white border rounded-xl p-6 shadow-lg max-w-md mx-auto transition-all duration-700 delay-500 ${heroLogin.isVisible ? 'animate-scale-in-delay-1' : 'opacity-0 scale-90'}`}>
                  <h3 className="text-lg font-semibold mb-4 text-center text-gray-800">
                    Comece Agora - É Gratuito
                  </h3>
                  <Login />
                </div>
                
                {/* Features */}
                
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section id="benefits" className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div ref={benefitsSection.ref} className={`text-center mb-16 transition-all duration-700 ${benefitsSection.isVisible ? 'animate-slide-up' : 'opacity-0 translate-y-8'}`}>
              <h2 className="text-5xl md:text-7xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent flex items-center justify-center gap-3 flex-wrap">
                Por que escolher o <img src="/images/justicai-text-logo.png" alt="Justiçai" className="h-[6rem] md:h-[6rem] w-auto inline-block" />?
              </h2>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
                Desenvolvido especificamente para o mercado jurídico brasileiro
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div ref={setBenefitRef(0)} className={`transition-all duration-700 delay-200 ${benefitsVisible[0] ? 'animate-slide-right' : 'opacity-0 -translate-x-8'}`}>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Shield className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-gray-900">100% Focado no Direito Brasileiro</h3>
                      <p className="text-gray-600">Nossa IA foi treinada especificamente com legislação, jurisprudência e práticas do sistema jurídico brasileiro.</p>
                    </div>
                  </div>
                </div>

                <div ref={setBenefitRef(1)} className={`transition-all duration-700 delay-400 ${benefitsVisible[1] ? 'animate-slide-right' : 'opacity-0 -translate-x-8'}`}>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-gray-900">Plano Gratuito Permanente</h3>
                      <p className="text-gray-600">Não é um período de teste limitado. Nosso plano gratuito é permanente e você pode usar para sempre.</p>
                    </div>
                  </div>
                </div>

                <div ref={setBenefitRef(2)} className={`transition-all duration-700 delay-600 ${benefitsVisible[2] ? 'animate-slide-right' : 'opacity-0 -translate-x-8'}`}>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-gray-900">Economia de 70% do Tempo</h3>
                      <p className="text-gray-600">Reduza drasticamente o tempo gasto em tarefas repetitivas como análise de contratos e criação de petições.</p>
                    </div>
                  </div>
                </div>

                <div ref={setBenefitRef(3)} className={`transition-all duration-700 delay-800 ${benefitsVisible[3] ? 'animate-slide-right' : 'opacity-0 -translate-x-8'}`}>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-gray-900">Suporte Especializado</h3>
                      <p className="text-gray-600">Nossa equipe entende as necessidades específicas dos profissionais do direito e oferece suporte dedicado.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm">
                      <span className="font-medium text-gray-900">Análise de Contratos</span>
                      <span className="text-sm text-green-600 font-semibold">-85% tempo</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm">
                      <span className="font-medium text-gray-900">Criação de Petições</span>
                      <span className="text-sm text-green-600 font-semibold">-70% tempo</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm">
                      <span className="font-medium text-gray-900">Resumo de Documentos</span>
                      <span className="text-sm text-green-600 font-semibold">-90% tempo</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm">
                      <span className="font-medium text-gray-900">Formatação de Documentos</span>
                      <span className="text-sm text-green-600 font-semibold">-95% tempo</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Hero Casos Section */}
        <HeroCasos />

        {/* Ethics Section */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-blue-600 to-indigo-700">
          <div className="container mx-auto px-4">
            <div ref={ethicsSection.ref} className={`text-center mb-12 transition-all duration-700 ${ethicsSection.isVisible ? 'animate-slide-up' : 'opacity-0 translate-y-8'}`}>
              <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full text-sm font-medium text-white mb-6">
                <Shield className="w-4 h-4 mr-2" />
                Práticas Éticas com IA
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Ajudamos a criar documentos seguindo as melhores práticas
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-white">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <Users className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold mb-2">Supervisão Profissional</h3>
                  <p className="text-sm text-white/80">Mantenha supervisão e assinatura profissional</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-white">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <Shield className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold mb-2">Proteção de Dados</h3>
                  <p className="text-sm text-white/80">Garanta sigilo e proteção de dados</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-white">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <MessageSquare className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold mb-2">Transparência</h3>
                  <p className="text-sm text-white/80">Informe o cliente sobre o uso da IA</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-white">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <FileCheck className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold mb-2">Conformidade</h3>
                  <p className="text-sm text-white/80">Atue de acordo com código de ética e legislação vigente</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Casos Features Section */}
        <CasosFeatures />

        {/* How It Works Section */}
        <section id="how-it-works" className="py-16 md:py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <div ref={howItWorksSection.ref} className={`text-center mb-16 transition-all duration-700 ${howItWorksSection.isVisible ? 'animate-slide-up' : 'opacity-0 translate-y-8'}`}>
              <h2 className="text-5xl md:text-7xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                Simples, rápido e eficiente
              </h2>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
                Em 3 passos você já está usando a IA para acelerar seu trabalho jurídico
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div ref={setStepRef(0)} className={`text-center transition-all duration-700 delay-200 ${stepsVisible[0] ? 'animate-slide-up-delay-1' : 'opacity-0 translate-y-8'}`}>
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Faça Login</h3>
                <p className="text-gray-600">Crie sua conta gratuita em segundos usando Google ou email. Não precisamos de cartão de crédito.</p>
              </div>

              <div ref={setStepRef(1)} className={`text-center transition-all duration-700 delay-400 ${stepsVisible[1] ? 'animate-slide-up-delay-2' : 'opacity-0 translate-y-8'}`}>
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Monte seu Caso</h3>
                <p className="text-gray-600">Acesse a página Casos e organize tudo em um só lugar: documentos, conversas com IA e informações do processo.</p>
              </div>

              <div ref={setStepRef(2)} className={`text-center transition-all duration-700 delay-600 ${stepsVisible[2] ? 'animate-slide-up-delay-3' : 'opacity-0 translate-y-8'}`}>
                <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Use a IA e Gere Documentos</h3>
                <p className="text-gray-600">Converse com a IA sobre o caso, gere peças jurídicas e tenha tudo organizado com histórico completo.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-16 md:py-24 bg-gray-900">
          <PlansSection />
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-blue-600 to-purple-700">
          <div className="container mx-auto px-4">
            <div ref={ctaSection.ref} className={`text-center transition-all duration-700 ${ctaSection.isVisible ? 'animate-slide-up' : 'opacity-0 translate-y-8'}`}>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Pronto para revolucionar sua prática jurídica?
              </h2>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Junte-se a milhares de advogados que já estão usando IA para acelerar seu trabalho
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-gray-100 text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg" onClick={() => document.getElementById('pricing')?.scrollIntoView({
                behavior: 'smooth'
              })}>
                  Começar Gratuitamente
                </Button>
                <Button size="lg" variant="outline" className="bg-white text-blue-600 px-8 py-4 text-lg" onClick={() => document.getElementById('pricing')?.scrollIntoView({
                behavior: 'smooth'
              })}>
                  Ver Planos
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-300 py-16">
          <div className="container mx-auto px-4">
            <div ref={footerSection.ref} className={`transition-all duration-700 ${footerSection.isVisible ? 'animate-slide-up' : 'opacity-0 translate-y-8'}`}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
                <div className="col-span-1 md:col-span-2">
                  <div className="flex items-center mb-4">
                    <img src="/logo_justicai_laib_landing.png" alt="Justiçai" className="h-10" />
                  </div>
                  <p className="text-gray-400 mb-6 max-w-md">Sua inteligência artificial jurídica que agiliza sua prática jurídica com análise de documentos, criação de peças processuais e compartilhe com sua equipe ou escritório.</p>
                  <div className="flex space-x-4">
                    <a href="#" className="text-gray-400 hover:text-white transition-colors">
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a href="#" className="text-gray-400 hover:text-white transition-colors">
                      <Linkedin className="w-5 h-5" />
                    </a>
                    <a href="mailto:contato@chat.adv.br" className="text-gray-400 hover:text-white transition-colors">
                      <Mail className="w-5 h-5" />
                    </a>
                  </div>
                </div>

                <div>
                  <h3 className="text-white font-semibold mb-4">Produto</h3>
                  <ul className="space-y-2">
                    <li><a href="#features" className="text-gray-400 hover:text-white transition-colors">Funcionalidades</a></li>
                    <li><a href="#pricing" className="text-gray-400 hover:text-white transition-colors">Preços</a></li>
                    <li><button onClick={() => document.getElementById('pricing')?.scrollIntoView({
                      behavior: 'smooth'
                    })} className="text-gray-400 hover:text-white transition-colors">Começar Gratuitamente</button></li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-white font-semibold mb-4">Legal</h3>
                  <ul className="space-y-2">
                    <li><button onClick={() => setShowTermsOfUse(true)} className="text-gray-400 hover:text-white transition-colors">Termos de Uso</button></li>
                    <li><button onClick={() => setShowPrivacyPolicy(true)} className="text-gray-400 hover:text-white transition-colors">Política de Privacidade</button></li>
                    <li><button onClick={() => setShowLGPDCompliance(true)} className="text-gray-400 hover:text-white transition-colors">LGPD</button></li>
                    <li><button onClick={() => setShowAITerms(true)} className="text-gray-400 hover:text-white transition-colors">Termos de IA</button></li>
                    <li><button onClick={() => setShowSecurityInfo(true)} className="text-gray-400 hover:text-white transition-colors">Segurança</button></li>
                  </ul>
                </div>
              </div>

              <div className="border-t border-sidebar-border mt-8 pt-8 text-center text-sidebar-foreground/70 space-y-3">
                <p>© 2026 Justiçai - Todos os direitos reservados.</p>
                <a href="https://laib.com.br" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity">
                  <img src="/images/logo-laib.png" alt="Laib" className="h-8" />
                  <span className="text-xs">Um produto Laib</span>
                </a>
              </div>
            </div>
          </div>
        </footer>

        {/* Welcome Dialog */}
        {showWelcome && <WelcomeDialog isOpen={showWelcome} onClose={() => {
        setShowWelcome(false);
        navigate('/painel');
      }} />}

        {/* Legal Document Dialogs */}
        <LegalDocumentDialog isOpen={showTermsOfUse} onClose={() => setShowTermsOfUse(false)} title="Termos de Uso">
          <TermsOfUseContent />
        </LegalDocumentDialog>

        <LegalDocumentDialog isOpen={showAITerms} onClose={() => setShowAITerms(false)} title="Termos de Uso da IA">
          <AITermsContent />
        </LegalDocumentDialog>

        <LegalDocumentDialog isOpen={showPrivacyPolicy} onClose={() => setShowPrivacyPolicy(false)} title="Política de Privacidade">
          <PrivacyPolicyContent />
        </LegalDocumentDialog>

        <LegalDocumentDialog isOpen={showLGPDCompliance} onClose={() => setShowLGPDCompliance(false)} title="Conformidade LGPD">
          <LGPDComplianceContent />
        </LegalDocumentDialog>

        <LegalDocumentDialog isOpen={showSecurityInfo} onClose={() => setShowSecurityInfo(false)} title="Informações de Segurança">
          <SecurityInfoContent />
        </LegalDocumentDialog>

        {showCookieConsent && <CookieConsentBanner onAccept={() => setShowCookieConsent(false)} onSettings={() => setShowPrivacyPolicy(true)} />}
      </div>
    </div>;
};
export default Index;